import React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import Avvvatars from 'avvvatars-react'
import { ChevronsUpDown } from 'lucide-react'
import Skeleton from 'react-loading-skeleton'
import { useOrgStore } from '@/stores/orgStore'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { Ripple } from '../magicui/ripple'

const apiUrl = import.meta.env.VITE_API_URL

interface Team {
  id: string
  name: string
  user_level: number
}

interface ApiResponse {
  limit: number
  organizations: Team[]
  page: number
  total: number
}

const userLevelMap: { [key: number]: string } = {
  0: 'Master',
  1: 'Admin',
  2: '',
  3: '',
}

// No changes needed in the useOrganizations hook – keep previous implementation
// eslint-disable-next-line react-refresh/only-export-components
export const useOrganizations = () => {
  const { getAccessTokenSilently } = useAuth0()
  const { setCurrentOrg } = useOrgStore()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['organizations'],
    queryFn: async (): Promise<ApiResponse> => {
      const accessToken = await getAccessTokenSilently()
      const response = await fetch(
        `${apiUrl}/user/organizations?page=1&limit=100`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Cache-Control': 'no-cache',
          },
        }
      )
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`)
      return response.json()
    },
    retry: true,
  })

  const refreshOrganizations = async () => {
    await queryClient.invalidateQueries({ queryKey: ['organizations'] })
    return query.refetch()
  }

  // New initialization logic
  const initializeOrganization = React.useCallback(
    (organizations: Team[]) => {
      const storedOrgId = localStorage.getItem('currentOrgId')
      const parsedStoredOrgId = storedOrgId ? JSON.parse(storedOrgId) : null
      const currentOrgId = useOrgStore.getState().currentOrgId
      const targetOrgId = currentOrgId || parsedStoredOrgId
      const validOrg = targetOrgId
        ? organizations.find((org) => org.id === targetOrgId)
        : null

      if (validOrg) {
        setCurrentOrg(validOrg.id, validOrg.user_level)
        localStorage.setItem('currentOrgId', JSON.stringify(validOrg.id))
      } else {
        const defaultOrg =
          organizations.find((org) => org.id === parsedStoredOrgId) || // Stored ID
          organizations.find((org) => org.user_level === 3) || // External-level org
          organizations.find((org) => org.user_level === 2) ||
          organizations.find((org) => org.user_level === 1) ||
          organizations.find((org) => org.user_level === 0) ||
          organizations[0] // Fallback: First org
        if (defaultOrg) {
          setCurrentOrg(defaultOrg.id, defaultOrg.user_level)
          localStorage.setItem('currentOrgId', JSON.stringify(defaultOrg.id))
        }
      }
    },
    [setCurrentOrg]
  )

  React.useEffect(() => {
    if (query.data?.organizations) {
      initializeOrganization(query.data.organizations)
    }
  }, [query.data, initializeOrganization])

  return {
    ...query,
    refreshOrganizations,
  }
}

export function TeamSwitcher() {
  const { isMobile } = useSidebar()
  const {
    data: orgData,
    isLoading,
    isError,
    refreshOrganizations, // Keep refresh capability
  } = useOrganizations()
  const { currentOrgId, setCurrentOrg } = useOrgStore()
  // Add initialization state tracking
  const [initializing, setInitializing] = React.useState(true)

  const teams = orgData?.organizations || []
  const activeTeam = teams.find((org) => org.id === currentOrgId) ?? null

  React.useEffect(() => {
    if (orgData?.organizations) {
      // Simulate initialization completion
      setInitializing(false)
    }
  }, [orgData])

  const switchOrganization = React.useCallback(
    async (team: Team) => {
      setCurrentOrg(team.id, team.user_level)
      //localStorage.setItem('currentOrgId', JSON.stringify(team.id))

      // Refresh organizations while maintaining current view
      await refreshOrganizations()
    },
    [setCurrentOrg, refreshOrganizations]
  )

  // Show skeleton during initial load
  if (isLoading) {
    return <Skeleton height={50} />
  }

  // Show full-page loader during post-fetch initialization
  if (initializing || (orgData && !activeTeam)) {
    return (
      <div className='fixed inset-0 flex items-center justify-center bg-white z-[99999] overflow-hidden'>
        <div className='relative h-full w-full flex items-center justify-center'>
          <Ripple />
        </div>
      </div>
    )
  }

  // Handle error or missing teams
  if (isError || !activeTeam) {
    return null
  }

  // Main render
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size='lg'
              className='rounded-full data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground p-2'
            >
              <div className='flex items-center w-full'>
                <div className='flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full overflow-hidden bg-gray-200'>
                  <Avvvatars value={activeTeam.name} style='character' />
                </div>

                <div className='flex-1 ml-4 text-left text-sm leading-tight truncate'>
                  <span className='block font-semibold truncate'>
                    {activeTeam.name}
                  </span>
                  <span className='block text-xs truncate'>
                    {userLevelMap[activeTeam.user_level] || ''}
                  </span>
                </div>

                <ChevronsUpDown className='ml-4 flex-shrink-0' />
              </div>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg'
            align='start'
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <DropdownMenuLabel className='text-xs text-muted-foreground'>
              Organizações
            </DropdownMenuLabel>
            {teams.map((team: Team) => (
              <DropdownMenuItem
                key={team.id}
                onClick={() => switchOrganization(team)}
                className='gap-2 p-2 focus:bg-purple-700'
              >
                <div className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'>
                  <Avvvatars value={team.name} style='character' />
                </div>
                <div className='flex-1'>
                  <span className='block truncate font-semibold'>
                    {team.name}
                  </span>
                  <span className='text-xs'>
                    {userLevelMap[team.user_level] || ''}
                  </span>
                </div>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
